<template>
  <div class="volunteer-card-container">
    <v-card
      class="modern-volunteer-card rounded-xl"
      :elevation="isHovered ? 3 : 1"
      @mouseover="isHovered = true"
      @mouseleave="isHovered = false"
      @click="goToProfile"
    >
      <!-- Loading state -->
      <v-skeleton-loader
        v-if="loading || !volunteer"
        type="image, list-item-two-line"
        class="pa-4"
      />

      <!-- Loaded state -->
      <template v-else>
        <!-- Card header with background and avatar -->
        <div class="card-header position-relative">
          <div class="header-bg">
            <img
              v-if="volunteer.bgImage"
              :src="volunteer.bgImage"
              alt="Volunteer Cover Image"
              class="w-100 h-100"
            />
            <div v-else class="default-bg"></div>
          </div>

          <v-chip
            :color="getButtonColor"
            small
            pill
            class="px-2 fan-badge"
            :txt-size="14"
            bold
          >
            <v-icon left size="16">{{ getButtonIcon }}</v-icon>
            <span class="caption">{{ getButtonText }}</span>
          </v-chip>
          <div class="avatar-container">
            <volunteer-avatar
              :img="volunteer.midThumbnail || volunteer.image"
              :name="volunteer.first_name + volunteer.last_name"
              :size="avatarSize"
              class="elevation-3"
            />
          </div>
        </div>

        <!-- Card content -->
        <div class="mt-2 text-center pa-4">
          <h3 class="mb-1 text-h6 font-weight-bold primary-color text-truncate">
            <!-- Show "Private Account" for locked profiles -->
            <span v-if="isPrivateAccount">
              Private Account
            </span>
            <span v-else>
              {{ volunteer.first_name + " " + volunteer.last_name }}
            </span>
            <v-icon
              v-if="isPrivateAccount"
              small
              color="grey darken-1"
              class="ml-1"
            >
              mdi-lock
            </v-icon>
          </h3>

          <!-- Show stats only if not private -->
          <div v-if="!isPrivateAccount" class="social-stats mb-4">
            <div class="stat-item">
              <div class="stat-value">{{ volunteer.followed_by?.length || 0 }}</div>
              <div class="stat-label">{{ volunteer.followed_by?.length === 1 ? $t("follower") : $t("followers") }}</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-value">{{ volunteer.followers?.length || 0 }}</div>
              <div class="stat-label">{{ $t("following") }}</div>
            </div>
          </div>

          <!-- Show lock message for private accounts -->
          <div v-else class="private-message">
            <v-icon small color="grey" class="mr-1">mdi-lock</v-icon>
            <span class="text-caption grey--text">This profile is private</span>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import VolunteerAvatar from "../../volunteer/VolunteerAvatar.vue";

export default {
  components: { VolunteerAvatar },
  props: {
    _id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      volunteer: null,
      isHovered: false,
      isPrivateAccount: false,
      errorMessage: null
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      type: "type"
    }),
    getFanned() {
      if (!this.profile?.fanned) return false;
      if (!this.volunteer) return false;
      return this.profile.fanned.includes(this.volunteer._id);
    },
    avatarSize() {
      const bp = this.$vuetify.breakpoint;
      if (bp.xs) return 50;
      if (bp.sm) return 60;
      return 70;
    },
    getButtonColor() {
      if (this.isPrivateAccount) return "grey";
      return this.getFanned ? "#fff" : "#fff";
    },
    getButtonIcon() {
      if (this.isPrivateAccount) return "mdi-lock";
      return this.getFanned ? "mdi-check" : "mdi-account-plus";
    },
    getButtonText() {
      if (this.isPrivateAccount) return this.$t("Locked");
      if (this.getFanned) return this.$t("Fan");
      return this.$t("Not Following you");
    }
  },
  methods: {
    ...mapActions("volunteer", {
      getVolunteer: "getVolunteer"
    }),
    goToProfile() {
      // Don't navigate if account is private
      if (this.isPrivateAccount) {
        return;
      }
      
      if (this.volunteer?._id) {
        this.$router.push({
          name: "volunteer-profile-details",
          params: { id: this.volunteer._id }
        });
      }
    },
      handleError(error) {
        const message = error.response?.data?.message || error.message || "";
        const status = error.response?.status;
        const code = error.response?.data?.code || "";

        const lockedMessages = ["Profile Is Locked", "Permission denied"];
        const lockedCodes = ["NOT_A_PARTICIPANT", "UNAUTHORIZED_EDIT", "PROFILE_LOCKED", "PERMISSION_DENIED"];

        const isLocked = status === 403 || lockedMessages.includes(message) || lockedCodes.includes(code);

        if (isLocked) {
          this.isPrivateAccount = true;
          this.volunteer = {
            _id: this._id,
            first_name: "Private",
            last_name: "User",
            fanned: [],
            followers: [],
            followed_by: []
          };
          this.errorMessage = message || "This profile is private";
          return;
        }

        // fallback for other errors
        console.error("Error loading volunteer:", message);
        this.$toast?.error(message || "Failed to load profile");
      }
  },
  mounted() {
    this.loading = true;
    this.getVolunteer({ _id: this._id })
      .then(res => {
        this.volunteer = res;
        this.isPrivateAccount = false;
        this.loading = false;
      })
      .catch(error => {
        this.loading = false;
        this.handleError(error);
      });
  }
};
</script>

<style scoped>
.volunteer-card-container {
  width: 100%;
  max-width: 300px;
  margin: auto;
  padding: 0 8px;
}

.modern-volunteer-card {
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.card-header {
  height: 100px;
  width: 100%;
  position: relative;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.default-bg {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a5b8a 0%, #0a7b8a 100%);
}

.avatar-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, 0%);
  z-index: 2;
  border-radius: 50%;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.primary-color {
  color: #472583;
}

.private-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  opacity: 0.7;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .modern-volunteer-card {
    padding: 12px;
  }
  .card-header {
    height: 90px;
  }
}

@media (max-width: 768px) {
  .volunteer-card-container {
    max-width: 90%;
  }
  .card-header {
    height: 80px;
  }
}

@media (max-width: 600px) {
  .volunteer-card-container {
    max-width: 100%;
    padding: 0 12px;
  }
  .card-header {
    height: 70px;
  }
  .avatar-container {
    top: 35px;
  }
  h3.text-h6 {
    font-size: 1rem;
  }
  .v-btn {
    font-size: 0.85rem;
  }
}

/* Grid layout for multiple cards */
.volunteer-cards-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 0 auto;
  padding: 16px;
}

.volunteer-card-container {
  flex: 1 1 calc(50% - 16px);
  max-width: 300px;
}

@media (max-width: 768px) {
  .volunteer-card-container {
    flex: 1 1 100%;
    max-width: 100%;
  }
}

span.px-2.fan-badge.v-chip.v-chip--pill.theme--light.v-size--small {
  margin: 23px 0px 0px 177px;
  border: 1px solid black;
}

/* Modern social stats */
.social-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
}
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 12px;
}
.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}
.stat-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}
.stat-divider {
  width: 1px;
  height: 24px;
  background: rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .volunteer-card-container {
    max-width: 90%;
  }
  .card-header {
    height: 80px;
  }
  .social-stats {
    gap: 8px;
  }
  .stat-item {
    padding: 0 8px;
  }
}
</style>